import React, {useEffect, useMemo, useState} from "react";
import {DivIcon, LatLngBounds} from "leaflet";
import {MapContainer, Marker, useMap} from "react-leaflet";
import ms from "milsymbol";
import {MapUnitModalTrigger} from "./MapUnitModalTrigger";
import {MapMarkerEditTrigger} from "./MapMarkerEditTrigger";
import {RootState} from "../../store";
import {useAppSelector, useAppDispatch, mapSlice, fetchMapUnits} from "./mapSlice";
import {fetchFactionMarkers, fetchGlobalMarkers, clearMarkers, markersSlice} from "../../store/markersSlice";
import {fetchFactionGraphics, fetchGlobalGraphics, clearGraphics, removeGraphic} from "../../store/graphicsSlice";
import * as graphicService from "../../services/graphicService";
import {setEditingGraphic, setGraphicAttachment} from "./mapUiSlice";
import ToolBox from "./toolbox/ToolBox";
import BaseLayers from "./BaseLayers";
import {MapViewMode, MapViewState} from "./MapViewTypes";
import './MapView.scss'
import {MapEntity} from "./mapTypes.d";
import {MgrsGraticule} from 'react-leaflet-mgrs-graticule';
import ExportRectangle from './export/ExportRectangle';
import TacticalDrawLayer from './TacticalDrawLayer';
import MarkerLayer from './MarkerLayer';
import GraphicsLayer from './GraphicsLayer';
import MarkerActionOverlay from './MarkerActionOverlay';
import GraphicActionOverlay from './GraphicActionOverlay';
import ConfirmDeleteModal from '../orbat/ConfirmDeleteModal';
import {mqttService} from "../../services/mqtt";
import {useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {buildDeletedMarkerCot, mapEntityToCotBytes} from "../../utils/cotUtils";
import {computeOpacity} from "./unitStalenessPolicy";
import {useStaleUnitTicker} from "./hooks";

/** Fits the map to the bounding box of all entities on faction change or initial load. */
function FitToEntities({ units, markers, factionId }: { units: MapEntity[]; markers: MapEntity[]; factionId: string | null }) {
    const map = useMap();
    const unitsRef = React.useRef(units);
    const markersRef = React.useRef(markers);
    unitsRef.current = units;
    markersRef.current = markers;

    useEffect(() => {
        // Small delay to let the fetch thunks populate state after faction switch
        const timer = setTimeout(() => {
            const allPoints = [...unitsRef.current, ...markersRef.current]
                .filter((e) => e.latLng.lat !== 0 || e.latLng.lng !== 0)
                .map((e): [number, number] => [e.latLng.lat, e.latLng.lng]);

            if (allPoints.length === 0) return;

            if (allPoints.length === 1) {
                map.setView(allPoints[0], 14);
                return;
            }

            const bounds = new LatLngBounds(allPoints);
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
        }, 500);
        return () => clearTimeout(timer);
    }, [factionId, map]); // Only triggers on faction change / mount

    return null;
}

export default function MapView() {
    const markers = useAppSelector((state: RootState) => state.map.value)
    const annotationMarkers = useAppSelector((state: RootState) => state.markers.value)
    const graphics = useAppSelector((state: RootState) => state.graphics.value)
    const editingGraphicId = useAppSelector((state: RootState) => state.mapUi.editingGraphicId)
    const mgrsGridEnabled = useAppSelector((state: RootState) => state.mapUi.mgrsGridEnabled)
    const activeLineSidc = useAppSelector((state: RootState) => state.mapUi.activeLineSidc)
    const { eventId } = useParams<{ eventId: string }>();
    const selectedFactionId = useSelector((state: RootState) => state.orbat.selectedFactionId);
    const [mapViewState, setMapViewState] = useState(new MapViewState())
    const [editMode, setEditMode] = useState(MapViewMode.BROWSE)
    const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null)
    const [nowMs, setNowMs] = useState(() => Date.now())
    const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null)
    const [graphicPendingDelete, setGraphicPendingDelete] = useState<string | null>(null)
    const [selectedGraphicId, setSelectedGraphicId] = useState<string | null>(null)
    const [markerPendingDelete, setMarkerPendingDelete] = useState<MapEntity | null>(null)
    const [markerPendingEdit, setMarkerPendingEdit] = useState<MapEntity | null>(null)
    const dispatch = useAppDispatch();

    const selectedMarker = useMemo(
        () => annotationMarkers.find((m) => m.id === selectedMarkerId) ?? null,
        [annotationMarkers, selectedMarkerId]
    );

    const handleSelectMarker = (marker: MapEntity) => {
        setSelectedMarkerId(marker.id);
    };

    const handleDismissOverlay = () => {
        setSelectedMarkerId(null);
    };

    const handleDeleteRequest = (markerId: string) => {
        const marker = annotationMarkers.find((m) => m.id === markerId);
        if (!marker) return;
        setMarkerPendingDelete(marker);
        setSelectedMarkerId(null);
    };

    const handleEditClick = (markerId: string) => {
        const marker = annotationMarkers.find((m) => m.id === markerId);
        if (!marker) return;
        setMarkerPendingEdit(marker);
        setSelectedMarkerId(null);
    };

    /**
     * "Draw line" on a marker: attach the next line to it, anchor the tail on
     * it, take its affiliation, and switch the map into Lines mode so the user
     * can aim the tip straight away. Mirrors the Android marker context menu,
     * where the same choice does the same things at once.
     */
    const handleDrawLineFromMarker = (markerId: string) => {
        const marker = annotationMarkers.find((m) => m.id === markerId);
        if (!marker) return;
        dispatch(setGraphicAttachment({
            kind: 'marker',
            id: markerId,
            anchor: { lat: marker.latLng.lat, lng: marker.latLng.lng },
            sidc: marker.code,
        }));
        setEditMode(MapViewMode.LINES);
        setSelectedMarkerId(null);
    };

    const handleCancelDelete = () => {
        setMarkerPendingDelete(null);
    };

    const handleConfirmDelete = () => {
        if (!markerPendingDelete || !eventId) {
            setMarkerPendingDelete(null);
            return;
        }

        const factionSegment = markerPendingDelete.faction_id ?? 'shared';
        const topic = `${eventId}/${factionSegment}/markers/deleted`;
        const cotBytes = buildDeletedMarkerCot(markerPendingDelete.id);
        mqttService.publish(topic, cotBytes);

        // Optimistic local removal — MarkersManager will also receive
        // the echoed MQTT message and dispatch removeMarker again. The
        // reducer is idempotent (filter by id), so the double-fire is
        // harmless.
        dispatch(markersSlice.actions.removeMarker(markerPendingDelete.id));
        setMarkerPendingDelete(null);
    };

    // Leaving Lines mode drops any pending attachment or in-flight redraw, so
    // neither can leak into a later standalone draw.
    useEffect(() => {
        if (editMode !== MapViewMode.LINES) {
            dispatch(setGraphicAttachment(null));
            dispatch(setEditingGraphic(null));
        }
    }, [editMode, dispatch]);

    // Sweep stale units out of the store every 15s (unit-only policy).
    useStaleUnitTicker();

    // Refresh `nowMs` on the same cadence so marker opacity decays visibly
    // during the 15m -> 30m fade window without needing per-marker state.
    useEffect(() => {
        const handle = setInterval(() => setNowMs(Date.now()), 15_000);
        return () => clearInterval(handle);
    }, []);

    // Load units and markers from backend when event/faction changes
    useEffect(() => {
        if (eventId && selectedFactionId) {
            dispatch(clearMarkers());
            dispatch(clearGraphics());
            dispatch(fetchMapUnits({ eventId, factionId: selectedFactionId }));
            dispatch(fetchFactionMarkers({ eventId, factionId: selectedFactionId }));
            dispatch(fetchGlobalMarkers({ eventId }));
            dispatch(fetchFactionGraphics({ eventId, factionId: selectedFactionId }));
            dispatch(fetchGlobalGraphics({ eventId }));
        }
    }, [eventId, selectedFactionId, dispatch]);

    // Clicking a line offers its actions rather than jumping straight to a
    // delete prompt — same contract as clicking a marker.
    const handleSelectGraphic = React.useCallback((graphicId: string) => {
        setSelectedGraphicId(graphicId);
    }, []);

    const selectedGraphic = React.useMemo(
        () => graphics.find((g) => g.id === selectedGraphicId) ?? null,
        [graphics, selectedGraphicId]
    );

    const handleDeleteGraphicRequest = (graphicId: string) => {
        setSelectedGraphicId(null);
        setGraphicPendingDelete(graphicId);
    };

    /**
     * "Edit" on a line is a redraw: reuse the drawing tool, then PATCH the
     * result onto the same row. The tool is the only way to express a shape, and
     * a half-drawn replacement must not be able to destroy the original — so the
     * old line stays on the server until the new shape is finished.
     */
    const handleEditGraphic = (graphicId: string) => {
        const graphic = graphics.find((g) => g.id === graphicId);
        if (!graphic) return;
        setSelectedGraphicId(null);
        // The redraw uses the graphic's own SIDC — `TacticalDrawLayer` reads it
        // off the line being edited. Pushing it into `activeLineSidc` instead
        // would leave the toolbox holding an affiliation the user never picked,
        // and the next standalone line would inherit it.
        dispatch(setEditingGraphic(graphicId));
        setEditMode(MapViewMode.LINES);
    };

    const handleConfirmDeleteGraphic = async () => {
        const graphicId = graphicPendingDelete;
        if (!graphicId || !eventId) return;
        setGraphicPendingDelete(null);
        // Optimistic removal; the backend also publishes a tombstone that
        // reaches every other client through GraphicsManager.
        dispatch(removeGraphic(graphicId));
        try {
            await graphicService.deleteGraphic(eventId, graphicId);
        } catch (error) {
            console.error('[graphics] failed to delete graphic:', error);
            // Put it back — the server still has it, so hiding it locally would
            // desync this client from everyone else.
            if (selectedFactionId) {
                dispatch(fetchFactionGraphics({ eventId, factionId: selectedFactionId }));
            }
            dispatch(fetchGlobalGraphics({ eventId }));
        }
    };

    const addEntity = (entity: MapEntity) => {
        dispatch(markersSlice.actions.addMarker(entity));
    }

    return (
        <div className={"leaflet-container"}>
            <ToolBox setTool={setEditMode} currentTool={editMode}/>
            <MapContainer
                center={[49.8175, 15.4730]}
                zoom={8}
                scrollWheelZoom={true}
                doubleClickZoom={false}
            >
                <MapUnitModalTrigger
                    handleNewMarker={addEntity}
                    editMode={editMode}
                />
                <MapMarkerEditTrigger
                    marker={markerPendingEdit}
                    onHide={() => setMarkerPendingEdit(null)}
                />
                <FitToEntities units={markers} markers={annotationMarkers} factionId={selectedFactionId} />
                <BaseLayers />
                <MgrsGraticule
                    name="MGRS Grid"
                    checked={mgrsGridEnabled}
                />
                <ExportRectangle />
                <TacticalDrawLayer active={editMode === MapViewMode.LINES} sidc={activeLineSidc} />
                <GraphicsLayer
                    onSelectGraphic={handleSelectGraphic}
                    hiddenGraphicId={editingGraphicId}
                />
                <GraphicActionOverlay
                    graphic={selectedGraphic}
                    onEdit={handleEditGraphic}
                    onDelete={handleDeleteGraphicRequest}
                    onDismiss={() => setSelectedGraphicId(null)}
                />
                <MarkerLayer
                    onSelectMarker={handleSelectMarker}
                    linesMode={editMode === MapViewMode.LINES}
                />
                <MarkerActionOverlay
                    marker={selectedMarker}
                    onDelete={handleDeleteRequest}
                    onEdit={handleEditClick}
                    onDrawLine={handleDrawLineFromMarker}
                    onDismiss={handleDismissOverlay}
                />
                {markers.filter((m) => m.latLng.lat !== 0 || m.latLng.lng !== 0).map((marker) =>
                    <Marker
                        key={marker.id}
                        position={marker.latLng}
                        draggable={marker.own}
                        opacity={computeOpacity(marker.lastUpdateMs, nowMs)}
                        icon={(() => {
                            const { dtg, ...renderProps } = marker.markerProperties || {} as any;
                            const isHovered = hoveredMarkerId === marker.id;
                            // Symbol label derived from the entity name at
                            // draw time; see MapEntity.name. Last wins.
                            const sym = new ms.Symbol(marker.code, {
                                size: 32,
                                ...renderProps,
                                ...(isHovered && dtg ? { dtg } : {}),
                                ...(marker.name ? { uniqueDesignation: marker.name } : {}),
                            });
                            const anchor = sym.getAnchor();
                            return new DivIcon({
                                html: sym.asSVG(),
                                iconAnchor: [anchor.x, anchor.y],
                                iconSize: [sym.getSize().width, sym.getSize().height],
                            });
                        })()}
                        eventHandlers={{
                            mouseover: () => setHoveredMarkerId(marker.id),
                            mouseout: () => setHoveredMarkerId(null),
                            // In Lines mode, clicking a unit chooses what the next
                            // drawn graphic attaches to (e.g. its line of movement).
                            // Leaflet markers don't bubble clicks to the map, so this
                            // never also drops a vertex. Clicking the same unit again
                            // detaches (the reducer toggles), so a standalone line is
                            // always reachable.
                            click: () => {
                                if (editMode !== MapViewMode.LINES) return;
                                dispatch(setGraphicAttachment({
                                    kind: 'unit',
                                    id: marker.id,
                                }));
                            },
                            moveend: (event: any) => {
                                let marker_copy = {...markers.find((m) => m.id === marker.id)};
                                if (marker_copy) {
                                    const ll = event.target._latlng;
                                    marker_copy.latLng = { lat: ll.lat, lng: ll.lng };
                                }
                                dispatch(mapSlice.actions.update(marker_copy));
                                // Publish CoT protobuf position update to faction MQTT topic
                                if (eventId && selectedFactionId && marker_copy && marker_copy.latLng) {
                                    const cotBytes = mapEntityToCotBytes(
                                        marker_copy as MapEntity,
                                        selectedFactionId
                                    );
                                    mqttService.publish(
                                        `${eventId}/${selectedFactionId}/sa/position`,
                                        cotBytes
                                    );
                                }
                            }
                        }}
                    >
                    </Marker>
                )}
            </MapContainer>
            <ConfirmDeleteModal
                show={!!markerPendingDelete}
                onHide={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Delete marker"
                message="Are you sure you want to delete this marker? This action cannot be undone."
            />
            <ConfirmDeleteModal
                show={!!graphicPendingDelete}
                onHide={() => setGraphicPendingDelete(null)}
                onConfirm={handleConfirmDeleteGraphic}
                title="Delete line"
                message="Are you sure you want to delete this tactical line? This action cannot be undone."
            />
        </div>
    );
}
